<template>
    <v-dialog v-model="dialog" width="600" persistent scrollable>
        <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="w-100">
                <slot></slot>
            </div>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                Configuration
                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-tabs v-model="tab">
                    <v-tab>Application settings</v-tab>
                    <v-tab>Add-ons</v-tab>
                    <v-tab v-if="nodepoolIsFractional">Share Allocation</v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab">
                    <v-tab-item>
                        <dashboard-loader class="mt-7" v-if="configLoading">Loading settings</dashboard-loader>

                        <div v-else>
                            <v-slider
                                label="Inactivity limit"
                                thumb-label="always"
                                :hint="limitHint"
                                max="24"
                                min="1"
                                v-model="inactivityLimit"
                                class="mt-10 mr-4"
                                persistent-hint
                                :disabled="isTrial"></v-slider>
                            <v-checkbox
                                hint="In shared mode multiple users can connect to the same application, allowing collaborative editing in certain applications (e.g. JupyterLab >= 4.0). Currently doesn't support querying tables."
                                label="Run in shared mode"
                                :disabled="!sharedEditable"
                                v-model="isShared"
                                persistent-hint></v-checkbox>
                            <v-checkbox
                                hint="Persisting session data enables you to retain data in the application's home folder (notably application-specific settings) between restarts. However, not persisting this data will usually come with extra performance benefit."
                                label="Persist sessions data in home area between restarts"
                                v-model="persistentHomeArea"
                                persistent-hint></v-checkbox>
                            <v-select
                                class="mt-10"
                                :items="availableIpcOptions"
                                label="Other applications may connect to this app from"
                                hint="IPC mode signal the Inter-Pod Communication mode for the app: “instance“ means any app in the same instance may connect to this app, “space“ means any app in the same space, and so on. The value “none“ means that no other application can connect to this app."
                                v-model="ipcMode"
                                hide-details
                                outlined></v-select>
                            <v-text-field
                                v-if="ipcMode === 'public'"
                                v-model="publicSubdomain"
                                class="mt-4"
                                label="Public subdomain"
                                hint="Public internet access for this app (REDCap only). Start with nv-pub-, must be unique across Nuvolos. Takes effect after stop & start; stop the app before changing it."
                                persistent-hint
                                outlined
                                :rules="[publicSubdomainRule]"
                                validate-on-blur></v-text-field>
                            <v-alert v-if="ipcMode !== 'none' && ipcMode !== 'public'" text color="info" class="mt-3 mb-0">
                                <v-row align="center">
                                    <v-col class="shrink"><v-icon color="info" right>mdi-connection</v-icon></v-col>
                                    <v-col class="grow">
                                        <p class="mb-4">Hostname of this application will be:</p>
                                        <div>
                                            <code class="pl-5 py-3">
                                                {{ ipcHostname }}
                                                <copy-to-clipboard
                                                    :textToCopy="ipcHostname"
                                                    buttonName="copy to clipboard"
                                                    :iconButton="true"></copy-to-clipboard>
                                            </code>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-alert>
                        </div>
                    </v-tab-item>
                    <v-tab-item>
                        <v-card flat tile>
                            <v-card-text class="px-1">
                                <v-alert type="info" text v-if="currentSpaceType === spaceTypes.EDUCATION_SPACE && !isSpaceAdmin">
                                    You need to be a
                                    <b>space administrator</b>
                                    to be able to add or remove add-ons.
                                </v-alert>
                                <v-expansion-panels v-model="expandedAddon">
                                    <v-expansion-panel v-for="(addon, idx) in addons" :key="idx">
                                        <v-expansion-panel-header disable-icon-rotate>
                                            <div>
                                                {{ addon.name }}
                                                <v-icon small v-if="expandedAddon === idx">mdi-chevron-up</v-icon>
                                                <v-icon small v-else>mdi-chevron-down</v-icon>
                                            </div>
                                            <v-spacer></v-spacer>
                                            <template v-slot:actions v-if="!addon.enabled">
                                                <v-btn
                                                    text
                                                    dense
                                                    color="primary"
                                                    @click.stop="enableAddon(addon)"
                                                    :disabled="!addon.editable || (currentSpaceType === spaceTypes.EDUCATION_SPACE && !isSpaceAdmin)"
                                                    :loading="loaders.includes(addon.name)">
                                                    Add
                                                </v-btn>
                                            </template>
                                            <template v-slot:actions v-else-if="addon.enabled && !addon.editable">
                                                <v-btn text dense color="error" disabled>Required</v-btn>
                                            </template>
                                            <template v-slot:actions v-else>
                                                <v-btn text dense :loading="loaders.includes(addon.name)" @click.stop="disableAddon(addon)">Remove</v-btn>
                                            </template>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <v-container>
                                                <div class="float-left mr-3" v-if="addon.icon_url">
                                                    <v-img max-width="4em" :src="addon.icon_url" />
                                                </div>
                                                <div class="caption" v-html="addon.description"></div>
                                                <div v-if="addon.doc_link" class="text-right mt-2">
                                                    <a :href="addon.doc_link" target="_blank">Go to documentation</a>
                                                </div>
                                            </v-container>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>
                    <v-tab-item v-if="nodepoolIsFractional">
                        <dashboard-loader class="mt-7" v-if="configLoading">Loading settings</dashboard-loader>
                        <div v-else class="pt-4">
                            <v-row dense>
                                <v-col cols="6" v-for="metric in resourceMetrics" :key="metric.label">
                                    <v-card flat class="grey lighten-3">
                                        <v-card-title class="caption pb-1">
                                            <v-icon small left :color="metric.color">{{ metric.icon }}</v-icon>
                                            {{ metric.label }}
                                        </v-card-title>
                                        <v-card-text class="text-h6 d-flex align-center">
                                            <template v-if="metric.before !== metric.after">
                                                <span class="grey--text text--darken-1 text-body-1">{{ metric.before }}{{ metric.unit }}</span>
                                                <v-icon small class="mx-1">mdi-arrow-right</v-icon>
                                            </template>
                                            <span>{{ metric.after }}{{ metric.unit }}</span>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-slider
                                label="Allocation"
                                color="primary"
                                track-color="grey lighten-2"
                                thumb-label="always"
                                thumb-color="primary"
                                hint="Define the share of the physical hardware dedicated to this environment. Adjust this to share the accelerator's compute power across multiple tasks."
                                max="100"
                                min="10"
                                step="10"
                                ticks="always"
                                tick-size="4"
                                v-model="resourceLimit"
                                class="mt-12 mr-4"
                                persistent-hint
                                :disabled="isTrial">
                                <template v-slot:thumb-label="{ value }">{{ value }}%</template>
                            </v-slider>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>
            <v-card-actions v-if="tab === 1"></v-card-actions>
            <v-card-actions v-else>
                <v-spacer />

                <v-btn @click="saveConfig" text color="primary" :loading="savingSettings" :disabled="!canSave">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { ipcOptions, isRedcapImage, publicSubdomainRule } from '@/utils'
import { enumsData } from '@/mixins/enums'
import { mapActions, mapGetters } from 'vuex'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as applicationErrors from '@/modules/application/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'

const { getBackendErrorMessage } = createBackendErrorHandler(applicationErrors, lifecycleErrors, permissionsErrors)

const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const DashboardLoader = () => import('@/components/DashboardLoader.vue')

export default {
    name: 'ApplicationConfigDialog',
    props: {
        aid: Number,
        canSave: Boolean,
        isTrial: {
            type: Boolean,
            default: false
        },
        // NC-3133: app row (snapshot applications payload incl.
        // image_families/image_description); gates the Public access option
        // to REDCap images (see availableIpcOptions).
        app: {
            type: Object,
            default: null
        }
    },
    mixins: [enumsData],
    components: {
        CopyToClipboard,
        DashboardLoader
    },
    data() {
        return {
            dialog: false,
            isShared: false,
            savingSettings: false,
            inactivityLimit: 1,
            persistentHomeArea: false,
            sharedEditable: false,
            ipcHostname: '',
            ipcOptions,
            ipcMode: ipcOptions[0].value,
            publicSubdomain: '',
            publicSubdomainRule,
            tab: 0,
            addons: [],
            loaders: [],
            expandedAddon: null,
            configLoading: false,
            resourceLimit: 100,
            originalResourceLimit: 100,
            fetchedApp: undefined,
            snapshot: undefined,
            nodepool: undefined,
            nodepoolIsFractional: false
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'currentSpaceType']),
        // NC-3133: Public access only for REDCap images (family names,
        // image description, or app_type of the app row). The shared
        // ipcOptions list is untouched so existing public configs display.
        availableIpcOptions() {
            const candidates = [this.app, this.fetchedApp].filter(Boolean)
            const imageHints = candidates.flatMap(candidate => [
                candidate,
                // eslint-disable-next-line camelcase
                ...(candidate.image_families || []).map(family_name => ({ family_name })),
                { description: candidate.image_description },
                { app_type: candidate.app_type }
            ])
            if (imageHints.some(image => isRedcapImage(image))) return ipcOptions
            return ipcOptions.filter(option => option.value !== 'public')
        },
        limitHint() {
            return this.isTrial
                ? 'Cannot change inactivity limit in trial.'
                : 'The number of inactive hours before the app gets shut down automatically (unscaled only).'
        },
        resourceMetrics() {
            const np = this.nodepool || {}
            const allocate = total => (total ? +((total * this.resourceLimit) / 100).toFixed(1) : 0)
            const allocateBefore = total => (total ? +((total * this.originalResourceLimit) / 100).toFixed(1) : 0)
            return [
                {
                    label: 'Processor',
                    icon: 'mdi-memory',
                    color: 'purple',
                    unit: '',
                    before: allocateBefore(np.cpu),
                    after: allocate(np.cpu)
                },
                {
                    label: 'RAM',
                    icon: 'mdi-memory',
                    color: 'purple',
                    unit: ' GB',
                    before: allocateBefore(np.memory),
                    after: allocate(np.memory)
                },
                {
                    label: 'Accelerator',
                    icon: 'mdi-memory mdi-rotate-45',
                    color: 'light-green',
                    unit: '%',
                    before: this.originalResourceLimit,
                    after: this.resourceLimit
                },
                {
                    label: 'HBM',
                    icon: 'mdi-memory mdi-rotate-45',
                    color: 'light-green',
                    unit: ' GB',
                    before: allocateBefore(np.vram),
                    after: allocate(np.vram)
                }
            ]
        }
    },
    methods: {
        ...mapActions('appStore', ['appGetByAID', 'deploymentGetByAID', 'nodepoolGetByShortId']),
        ...mapActions('snapshotStore', ['snapshotGetByAID']),
        saveConfig() {
            // NC-3133: fast client-side format check; reserved/profanity/
            // uniqueness stay server-side (their messages come from errors.js).
            if (this.ipcMode === 'public' && this.publicSubdomainRule(this.publicSubdomain) !== true) {
                this.$store.dispatch('showSnackBar', { snackBarText: this.publicSubdomainRule(this.publicSubdomain), snackBarIcon: 'error' })
                return
            }
            this.savingSettings = true
            this.$axios
                .put(`/applications/${this.aid}/config`, {
                    NCU: this.NCU,
                    shared: this.isShared,
                    inactivity_limit: this.inactivityLimit,
                    home_mount_type: this.persistentHomeArea ? 'ceph_direct' : 'emptydir',
                    shared_editable: this.sharedEditable,
                    ipc_mode: this.ipcMode,
                    public_subdomain: this.ipcMode === 'public' ? this.publicSubdomain.trim().toLowerCase() : null,
                    hami_resource_limit: this.resourceLimit
                })
                .then(() => {
                    this.dialog = false
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Settings saved, stop & start the application for it to take effect.',
                        snackBarIcon: 'check_circle'
                    })
                })
                .catch(err => {
                    console.log(err)
                    const snackBarText = getBackendErrorMessage(err, 'There was an error whilst saving settings')
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', { snackBarText, snackBarIcon: 'error' })
                    }
                })
                .finally(() => {
                    this.savingSettings = false
                })
        },
        fetchConfig() {
            this.configLoading = true
            this.$axios
                .get(`/applications/${this.aid}/config`)
                .then(({ data }) => {
                    this.isShared = data.shared
                    this.inactivityLimit = data.inactivity_limit
                    this.persistentHomeArea = data.home_mount_type === 'ceph_direct'
                    this.sharedEditable = data.shared_editable
                    this.addons = data.addons
                    this.ipcHostname = data.ipc_hostname
                    this.ipcMode = data.ipc_mode
                    this.publicSubdomain = data.public_subdomain || ''
                    this.resourceLimit = data.hami_resource_limit || 100
                    this.originalResourceLimit = this.resourceLimit
                })
                .catch(err => {
                    console.log(err)
                    const snackBarText = getBackendErrorMessage(err, 'There was an error whilst fetching application settings')
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', { snackBarText, snackBarIcon: 'error' })
                    }
                })
            this.configLoading = false
        },
        enableAddon(addon) {
            this.sendAddonUpdate(addon, true)
        },
        disableAddon(addon) {
            this.sendAddonUpdate(addon, false)
        },
        sendAddonUpdate(addon, enable) {
            this.loaders.push(addon.name)
            addon.enabled = enable
            this.$axios
                .patch(`/applications/${this.aid}/config`, { addons: [addon] })
                .then(() => {
                    this.fetchConfig()
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: `${addon.name} successfully ${addon.enabled ? 'added. It will be available after restart.' : 'removed.'}`,
                        snackBarIcon: 'check_circle'
                    })
                })
                .catch(error => {
                    console.error(error)
                    const snackBarText = getBackendErrorMessage(error, 'There was an error enabling addon')
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', { snackBarText, snackBarIcon: 'error' })
                    }
                })
                .finally(() => {
                    this.loaders = this.loaders.filter(loader => loader !== addon.name)
                })
        }
    },
    watch: {
        dialog(open) {
            if (!open) return
            this.fetchConfig()
        }
    },
    mounted() {
        this.appGetByAID(this.aid)
            .then(app => {
                this.fetchedApp = app
                return this.snapshotGetByAID(this.aid)
            })
            .then(snapshot => {
                this.snapshot = snapshot
                // Resolve the pool record first; Azure uses `-hami` suffix,
                // Switch tenant GPU pools (cloud switch + gpu_type) are HAMi
                // by construction and carry no suffix.
                return this.nodepoolGetByShortId(this.snapshot.node_pool)
            })
            .then(nodepool => {
                const name = (this.snapshot && this.snapshot.node_pool) || ''
                const isHamiName = typeof name.includes === 'function' && name.includes('-hami')
                const isSwitchGpu = Boolean(nodepool && nodepool.cloud === 'switch' && nodepool.gpu_type)
                this.nodepoolIsFractional = Boolean(isHamiName || isSwitchGpu)
                this.nodepool = this.nodepoolIsFractional ? nodepool : undefined
            })
            .catch(err => {
                console.error(err)
                this.fetchedApp = null
            })
    }
}
</script>
